import { createFileRoute, Link } from '@tanstack/react-router'
import { HomeGrid, HomeJourney, PageStrip, isDemoSession, useEntitledProducts } from '@bloo-team/core-shell'

/**
 * Home, in two tabs (`docs/home-journey-requirements.md` J1):
 *
 * - **Journey** (default) — where this deployment sits on the path from
 *   installed to mature, what to do next, and the scorecard behind it. Core
 *   composes it from the entitled manifests' `maturity` signals exactly as it
 *   composes the grid from their `home` widgets; core reads nothing itself
 *   (rule 11).
 * - **Operations** — the live bento, unchanged: every entitled product's
 *   `home` widgets in global `order` rank (D4/D7, `docs/home.md`).
 *
 * Two jobs on two cadences — "are we maturing" is a weekly read, "what is
 * happening now" is a every-morning one — so they are tabs rather than one
 * page that reshuffles as a score moves. The choice lives in the URL so a
 * link lands on either and e2e can address both.
 */

type HomeTab = 'journey' | 'operations'

const TABS: ReadonlyArray<{ id: HomeTab; label: string }> = [
  { id: 'journey', label: 'Journey' },
  { id: 'operations', label: 'Operations' },
]

/**
 * `tab` is OPTIONAL, and its absence means Journey. Making it required put a
 * mandatory search param on every `to: '/$clusterId/$scopeId/home'` in the
 * app — login, the handoff redeem, the root redirect — for a default the
 * route can supply itself.
 */
export interface HomeSearch {
  tab?: HomeTab
}

function Home() {
  const { clusterId, scopeId } = Route.useParams()
  const { tab = 'journey' } = Route.useSearch()
  const entitled = useEntitledProducts(clusterId, scopeId)

  return (
    <div>
      <PageStrip
        title="Home"
        meta={`${scopeId}${tab === 'operations' ? ' · last 24h' : ''}${isDemoSession() ? ' · demo data' : ''}`}
        end={
          <nav className="home-tabs" aria-label="Home views">
            {TABS.map((t) => (
              <Link
                key={t.id}
                to="/$clusterId/$scopeId/home"
                params={{ clusterId, scopeId }}
                search={t.id === 'journey' ? {} : { tab: t.id }}
                className="home-tab"
                data-on={tab === t.id ? '' : undefined}
                aria-current={tab === t.id ? 'page' : undefined}
              >
                {t.label}
              </Link>
            ))}
          </nav>
        }
      />
      {tab === 'journey' ? (
        <HomeJourney clusterId={clusterId} scopeId={scopeId} entitled={entitled} />
      ) : (
        <HomeGrid clusterId={clusterId} scopeId={scopeId} entitled={entitled} />
      )}
    </div>
  )
}

export const Route = createFileRoute('/$clusterId/$scopeId/home')({
  // Journey is the default: a deployment that has not finished arriving needs
  // the path more than it needs today's totals, and a mature one is one click
  // from the bento it already knows.
  validateSearch: (search: Record<string, unknown>): HomeSearch =>
    search.tab === 'operations' ? { tab: 'operations' } : {},
  component: Home,
})
